import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  FolderIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  MapIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import DailyDataNav from './DailyDataNav'
import DailyRunTimeField from '../../components/daily-data/DailyRunTimeField'
import { formatSydneyDateTime } from '../../utils/sydneyDateTime'
import {
  dailyCronToTimeValue,
  formatDailyCronLabel,
  sortByDailyCron,
  timeValueToDailyCron,
} from '../../utils/dailyCronSchedule'

const FEED_TEMPLATES = [
  {
    key: 'visitor_template_id',
    mappingType: 'visitor',
    label: 'Visitors (file)',
    defaultLabel: 'IMPORT_MAPPING_DEFAULT_VISITOR',
    feedPath: '/monitor-safe/daily-data/visitor',
  },
  {
    key: 'egm_template_id',
    mappingType: 'egm',
    label: 'EGM registry (DimMachine)',
    defaultLabel: 'IMPORT_MAPPING_DEFAULT_EGM',
    feedPath: '/monitor-safe/daily-data/egm',
  },
  {
    key: 'patron_template_id',
    mappingType: 'patron',
    label: 'DimPatron',
    defaultLabel: 'IMPORT_MAPPING_DEFAULT_PATRON',
    feedPath: '/monitor-safe/daily-data/dimpatron',
  },
  {
    key: 'sessions_template_id',
    mappingType: 'sessions',
    label: 'Gaming sessions',
    defaultLabel: 'IMPORT_MAPPING_DEFAULT_SESSIONS',
    feedPath: '/monitor-safe/daily-data/sessions',
  },
  {
    key: 'meter_template_id',
    mappingType: 'meter',
    label: 'Meter readings',
    defaultLabel: 'IMPORT_MAPPING_DEFAULT_METER',
    feedPath: '/monitor-safe/daily-data/meter',
  },
  {
    key: 'slot_analysis_template_id',
    mappingType: 'slot_analysis',
    label: 'Slot analysis (FactSlotAnalysisDaily)',
    defaultLabel: 'IMPORT_MAPPING_DEFAULT_SLOT_ANALYSIS',
    feedPath: '/monitor-safe/daily-data/slot-analysis',
  },
  {
    key: 'tito_template_id',
    mappingType: 'tito',
    label: 'TITO Data',
    defaultLabel: 'IMPORT_MAPPING_DEFAULT_TITO',
    feedPath: '/monitor-safe/daily-data/tito',
  },
]

const IMPORT_FEED_SCHEDULES = [
  { id: 'egm', field: 'egm_schedule', label: 'DimMachine / EGM', fallback: '05:45' },
  { id: 'patron', field: 'patron_schedule', label: 'DimPatron', fallback: '06:00' },
  { id: 'visitor', field: 'visitor_schedule', label: 'Visitor', fallback: '06:15' },
  { id: 'sessions', field: 'sessions_schedule', label: 'Gaming sessions', fallback: '06:30' },
  { id: 'meter', field: 'meter_schedule', label: 'Meter readings', fallback: '07:00' },
]

const RULES_SCHEDULE_ITEM = {
  id: 'rules',
  field: 'rules_schedule',
  label: 'Monitor Safe rules (after ingest)',
  fallback: '08:30',
}

const COMBINED_SCHEDULE_ITEM = {
  id: 'combined',
  field: 'schedule',
  label: 'All feeds ingest',
  fallback: '06:00',
}

function isCombinedScheduleMode(form) {
  return (form?.import_schedule_mode || 'staggered') === 'combined'
}

function statusBadge(st) {
  const map = {
    success: 'bg-green-100 text-green-800',
    partial: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
    skipped: 'bg-gray-100 text-gray-800',
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[st] || 'bg-gray-100 text-gray-800'}`}>
      {st || 'unknown'}
    </span>
  )
}

export default function DailyDataSettings() {
  const [tab, setTab] = useState('schedule')
  const [config, setConfig] = useState(null)
  const [history, setHistory] = useState([])
  const [status, setStatus] = useState(null)
  const [templatesByType, setTemplatesByType] = useState({ patron: [], sessions: [], meter: [], egm: [], visitor: [], slot_analysis: [], tito: [] })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)
  const [runningRules, setRunningRules] = useState(false)
  const [message, setMessage] = useState(null)
  const [form, setForm] = useState({})
  const [forbidden, setForbidden] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setForbidden(false)
    try {
      const [cfgRes, histRes, statusRes, patronT, sessionsT, meterT, egmT, visitorT, slotAnalysisT, titoT] = await Promise.all([
        authFetch(getLocalApiUrl('/api/admin/import/config')),
        authFetch(getLocalApiUrl('/api/admin/import/history?limit=50')),
        authFetch(getLocalApiUrl('/api/admin/import/status')),
        authFetch(getLocalApiUrl('/api/import-mapping-templates?mapping_type=patron')),
        authFetch(getLocalApiUrl('/api/import-mapping-templates?mapping_type=sessions')),
        authFetch(getLocalApiUrl('/api/import-mapping-templates?mapping_type=meter')),
        authFetch(getLocalApiUrl('/api/import-mapping-templates?mapping_type=egm')),
        authFetch(getLocalApiUrl('/api/import-mapping-templates?mapping_type=visitor')),
        authFetch(getLocalApiUrl('/api/import-mapping-templates?mapping_type=slot_analysis')),
        authFetch(getLocalApiUrl('/api/import-mapping-templates?mapping_type=tito')),
      ])

      if (cfgRes.status === 403) {
        setForbidden(true)
        return
      }

      const cfgJson = await cfgRes.json()
      const histJson = await histRes.json()
      const statusJson = await statusRes.json()

      if (cfgJson.success) {
        setConfig(cfgJson.data)
        setForm(cfgJson.data)
      } else {
        setMessage({ type: 'error', text: cfgJson.error || 'Failed to load config' })
      }
      if (histJson.success) setHistory(histJson.data || [])
      if (statusJson.success) setStatus(statusJson.data)

      const parseTemplates = async (res) => {
        if (!res.ok) return []
        const json = await res.json()
        return json.success ? (json.data || []) : []
      }
      setTemplatesByType({
        patron: await parseTemplates(patronT),
        sessions: await parseTemplates(sessionsT),
        meter: await parseTemplates(meterT),
        egm: await parseTemplates(egmT),
        visitor: await parseTemplates(visitorT),
        slot_analysis: await parseTemplates(slotAnalysisT),
        tito: await parseTemplates(titoT),
      })
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings: ' + err.message })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const tenantTimezone = form.timezone || config?.timezone || 'Australia/Sydney'

  const combinedMode = isCombinedScheduleMode(form)

  const sortedImportSchedules = useMemo(
    () => sortByDailyCron(
      IMPORT_FEED_SCHEDULES,
      (item) => form[item.field],
      (item) => item.fallback,
    ),
    [
      form.egm_schedule,
      form.patron_schedule,
      form.visitor_schedule,
      form.sessions_schedule,
      form.meter_schedule,
    ],
  )

  const sortedRunOrder = useMemo(
    () => sortByDailyCron(
      combinedMode
        ? [COMBINED_SCHEDULE_ITEM, RULES_SCHEDULE_ITEM]
        : [...IMPORT_FEED_SCHEDULES, RULES_SCHEDULE_ITEM],
      (item) => form[item.field],
      (item) => item.fallback,
    ),
    [
      combinedMode,
      form.schedule,
      form.egm_schedule,
      form.patron_schedule,
      form.visitor_schedule,
      form.sessions_schedule,
      form.meter_schedule,
      form.rules_schedule,
    ],
  )

  const displayRunOrder = useMemo(() => {
    if (!form.rules_after_pipeline) return sortedRunOrder
    return [...sortedRunOrder.filter((item) => item.id !== 'rules'), RULES_SCHEDULE_ITEM]
  }, [sortedRunOrder, form.rules_after_pipeline])

  const setScheduleMode = (mode) => {
    if (mode === 'combined') {
      setForm((prev) => ({
        ...prev,
        import_schedule_mode: 'combined',
        schedule: prev.schedule || timeValueToDailyCron(dailyCronToTimeValue(null, '06:00')),
      }))
    } else {
      updateForm('import_schedule_mode', 'staggered')
    }
  }

  const saveConfig = async () => {
    setSaving(true)
    setMessage(null)
    try {
      const payload = {
        enabled: form.enabled,
        import_directory: form.import_directory,
        schedule: form.schedule,
        timezone: form.timezone,
        venue_code: form.venue_code,
        egm_enabled: form.egm_enabled,
        patron_enabled: form.patron_enabled,
        sessions_enabled: form.sessions_enabled,
        meter_enabled: form.meter_enabled,
        slot_analysis_enabled: form.slot_analysis_enabled,
        tito_enabled: !!form.tito_enabled,
        visitor_enabled: form.visitor_enabled,
        visitor_source: form.visitor_source || null,
        import_archived_members: form.import_archived_members,
        egm_template_id: form.egm_template_id ? parseInt(form.egm_template_id, 10) : null,
        visitor_template_id: form.visitor_template_id ? parseInt(form.visitor_template_id, 10) : null,
        patron_template_id: form.patron_template_id ? parseInt(form.patron_template_id, 10) : null,
        sessions_template_id: form.sessions_template_id ? parseInt(form.sessions_template_id, 10) : null,
        meter_template_id: form.meter_template_id ? parseInt(form.meter_template_id, 10) : null,
        slot_analysis_template_id: form.slot_analysis_template_id ? parseInt(form.slot_analysis_template_id, 10) : null,
        tito_template_id: form.tito_template_id ? parseInt(form.tito_template_id, 10) : null,
        egm_schedule: form.egm_schedule || null,
        patron_schedule: form.patron_schedule || null,
        visitor_schedule: form.visitor_schedule || null,
        sessions_schedule: form.sessions_schedule || null,
        meter_schedule: form.meter_schedule || null,
        require_patron_before_gaming: form.require_patron_before_gaming !== false,
        rules_schedule: form.rules_schedule
          || timeValueToDailyCron(dailyCronToTimeValue(form.rules_schedule)),
        rules_schedule_enabled: form.rules_schedule_enabled === true,
        rules_after_pipeline: form.rules_after_pipeline === true,
        rules_require_ready: form.rules_require_ready !== false,
        auto_pipeline_enabled: form.auto_pipeline_enabled === true,
        import_schedule_mode: form.import_schedule_mode === 'combined' ? 'combined' : 'staggered',
        source_type: form.source_type || 'local',
        azure_blob_container: form.azure_blob_container || 'tsg',
        azure_blob_prefix: form.azure_blob_prefix || 'TPDataExtracts/',
        trading_day_offset: form.trading_day_offset != null ? parseInt(form.trading_day_offset, 10) : 1,
      }
      const res = await authFetch(getLocalApiUrl('/api/admin/import/config'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await res.json()
      if (json.success) {
        setConfig(json.data)
        setForm(json.data)
        setMessage({ type: 'success', text: 'Configuration saved and scheduler updated' })
      } else {
        setMessage({ type: 'error', text: json.detail || json.error || 'Failed to save' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Save failed: ' + err.message })
    } finally {
      setSaving(false)
    }
  }

  const runNow = async () => {
    if (!confirm('Run all enabled imports now for this tenant?')) return
    setRunning(true)
    setMessage(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/import/run'), { method: 'POST' })
      const json = await res.json()
      if (json.success) {
        setMessage({
          type: 'success',
          text: `Import completed in ${json.data.duration_ms}ms — ${json.data.errors?.length || 0} errors`,
        })
        fetchAll()
      } else {
        setMessage({ type: 'error', text: json.error || 'Import failed' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Run failed: ' + err.message })
    } finally {
      setRunning(false)
    }
  }

  const runRulesNow = async () => {
    if (!confirm('Run published Monitor Safe rules for the scheduled trading day now?')) return
    setRunningRules(true)
    setMessage(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/import/run-rules'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const json = await res.json()
      if (json.success) {
        setMessage({
          type: 'success',
          text: `Rules run OK for ${json.data?.tradingDay || 'trading day'}${json.data?.duration_ms != null ? ` (${json.data.duration_ms}ms)` : ''}`,
        })
        fetchAll()
      } else if (json.data?.skipped) {
        setMessage({
          type: 'error',
          text: `Rules skipped: ${json.data.reason || json.error || 'not ready'}`,
        })
        fetchAll()
      } else {
        setMessage({ type: 'error', text: json.error || 'Rules run failed' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Rules run failed: ' + err.message })
    } finally {
      setRunningRules(false)
    }
  }

  const formatDateTime = (ts) => (ts ? formatSydneyDateTime(new Date(ts)) : '—')

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (forbidden) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Link to="/monitor-safe/daily-data" className="inline-flex items-center hover:text-gray-900">
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Daily Data
          </Link>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          You need <strong>admin/system:view</strong> permission to manage Daily Data settings.
          You can still run imports from each feed page under Monitor Safe → Daily Data.
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'schedule', name: 'Schedule & source', icon: CalendarDaysIcon },
    { id: 'templates', name: 'Mapping templates', icon: MapIcon },
    { id: 'history', name: 'Import history', icon: ClockIcon },
  ]

  return (
    <div className="space-y-6">
      <DailyDataNav />

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Daily Data settings</h1>
            <p className="text-gray-600 mt-1">
              Scheduler, Azure source, and vendor mapping templates for DimMachine, DimPatron, sessions, and meter.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchAll}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              <ArrowPathIcon className="w-4 h-4 mr-1" />
              Refresh
            </button>
            <button
              type="button"
              onClick={runNow}
              disabled={running || !config?.enabled}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              <PlayIcon className="w-4 h-4 mr-1" />
              {running ? 'Running…' : 'Run all now'}
            </button>
            <button
              type="button"
              onClick={runRulesNow}
              disabled={runningRules}
              className="inline-flex items-center px-4 py-2 border border-primary-600 text-primary-700 rounded-md text-sm font-medium hover:bg-primary-50 disabled:opacity-50"
            >
              <PlayIcon className="w-4 h-4 mr-1" />
              {runningRules ? 'Rules…' : 'Run rules now'}
            </button>
          </div>
        </div>
        {status && (
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <span className={`w-2 h-2 rounded-full ${status.scheduled ? 'bg-green-500' : 'bg-gray-400'}`} />
              <span className="text-gray-600">
                {status.scheduled ? 'Scheduled' : 'Not scheduled'}
                {status.import_schedule_mode === 'combined'
                  ? ' (single daily run)'
                  : status.workerManaged || status.mode === 'staggered'
                    ? ' (multi-feed)'
                    : ''}
                {status.workerManaged ? ' · PM2 worker' : ''}
              </span>
            </div>
            {status.mode === 'staggered' && status.feeds && (
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                {['egm', 'patron', 'visitor', 'sessions', 'meter'].map(feed => (
                  <span key={feed}>
                    {feed}: {status.feeds[feed]?.schedule || '—'}
                    {status.feeds[feed]?.running ? ' (running)' : ''}
                  </span>
                ))}
              </div>
            )}
            {status.rules && (
              <div className="text-xs text-gray-500">
                rules:{' '}
                {status.rules.enabled
                  ? formatDailyCronLabel(status.rules.schedule, { timezone: form.timezone || config?.timezone })
                  : 'off'}
                {status.rules.running ? ' (running)' : ''}
                {status.rules.last_run_status ? ` · last ${status.rules.last_run_status}` : ''}
              </div>
            )}
            {status.running && (
              <div className="flex items-center gap-1 text-blue-600">
                <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600" />
                <span>Currently running</span>
              </div>
            )}
            <div className="text-gray-500">
              Last run: <span className="font-medium">{formatDateTime(config?.last_run_at)}</span>
            </div>
            {config?.last_run_status && statusBadge(config.last_run_status)}
          </div>
        )}
      </div>

      {message && (
        <div className={`rounded-lg p-4 ${
          message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex">
            {message.type === 'success'
              ? <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
              : <XCircleIcon className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />}
            <p className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
              {message.text}
            </p>
          </div>
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-6">
          {tabs.map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`flex items-center py-2 px-1 border-b-2 text-sm font-medium ${
                tab === t.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <t.icon className="w-4 h-4 mr-2" />
              {t.name}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'schedule' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Cog6ToothIcon className="w-5 h-5 text-gray-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Schedule &amp; source</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">Import scheduler enabled</div>
                <div className="text-sm text-gray-500">Master switch for cron imports</div>
              </div>
              <button
                type="button"
                onClick={() => updateForm('enabled', !form.enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  form.enabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  form.enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {form.enabled && (
              <div className="md:col-span-2 p-4 rounded-lg border border-gray-200 bg-gray-50/80">
                <div className="text-sm font-medium text-gray-900 mb-1">How should daily ingest run?</div>
                <p className="text-xs text-gray-500 mb-3">
                  Choose one schedule style. Both run on the import worker when the scheduler is enabled.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setScheduleMode('staggered')}
                    className={`text-left p-3 rounded-lg border-2 transition ${
                      !combinedMode
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">Multi-feed schedule</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Separate time per feed (EGM, DimPatron, sessions, meter, …). Feeds run independently.
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setScheduleMode('combined')}
                    className={`text-left p-3 rounded-lg border-2 transition ${
                      combinedMode
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">Single daily run</div>
                    <div className="text-xs text-gray-500 mt-1">
                      One time — all feeds run together in order (like &ldquo;Run all now&rdquo;).
                    </div>
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FolderIcon className="w-4 h-4 inline mr-1" />
                Local import directory
              </label>
              <input
                type="text"
                value={form.import_directory || ''}
                onChange={(e) => updateForm('import_directory', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                placeholder="/var/paysafego/imports"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Visitor source</label>
              <select
                value={form.visitor_source || ''}
                onChange={(e) => updateForm('visitor_source', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">Auto (RXConnect when configured)</option>
                <option value="api">RXConnect API</option>
                <option value="file">File extract (DimVisitor)</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-2">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                {combinedMode ? 'Single ingest time' : 'Import run order'}
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                {combinedMode
                  ? 'All enabled feeds run in one job at this time (EGM → Patron → Visitor → Sessions → Meter → TITO), then combine.'
                  : 'Each feed fires at its own time. List is sorted chronologically — change a time and the order updates.'}
              </p>
              {combinedMode ? (
                <div className="p-3 rounded-lg border border-gray-100 bg-gray-50/50">
                  <DailyRunTimeField
                    label="Daily ingest run time"
                    value={form.schedule}
                    onChange={(cron) => updateForm('schedule', cron)}
                    timezone={tenantTimezone}
                    fallback="06:00"
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  {sortedImportSchedules.map((feed) => (
                    <div key={feed.id} className="flex gap-3 items-start p-3 rounded-lg border border-gray-100 bg-gray-50/50">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold mt-1">
                        {Math.max(1, sortedRunOrder.findIndex((x) => x.id === feed.id) + 1)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <DailyRunTimeField
                          label={`${feed.label} run time (daily)`}
                          value={form[feed.field]}
                          onChange={(cron) => updateForm(feed.field, cron)}
                          timezone={tenantTimezone}
                          fallback={feed.fallback}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="md:col-span-2 rounded-lg border border-gray-200 bg-white p-3">
              <div className="text-xs font-medium text-gray-700 mb-2">Full day order (updates when times change)</div>
              <div className="flex flex-wrap gap-x-1 gap-y-1 text-xs text-gray-600">
                {displayRunOrder.map((item, index) => (
                  <span key={item.id}>
                    {index > 0 ? <span className="text-gray-400 mx-1">→</span> : null}
                    <span className="font-medium text-gray-800">{index + 1}.</span>
                    {' '}
                    {item.label}
                    {' '}
                    <span className="text-gray-500">
                      ({item.id === 'rules' && form.rules_after_pipeline
                        ? 'after combine finishes'
                        : formatDailyCronLabel(
                          form[item.field] || timeValueToDailyCron(item.fallback),
                          { timezone: tenantTimezone },
                        )})
                    </span>
                  </span>
                ))}
              </div>
            </div>

            <div className="md:col-span-2 border-t border-gray-200 pt-4 mt-2">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">After ingest — combine &amp; rules</h3>
              <p className="text-xs text-gray-500 mb-3">
                {combinedMode
                  ? 'Optionally run combine at the end of the single ingest job, then rules.'
                  : 'After the last import feed (usually meter), optionally run combine automatically, then rules.'}
              </p>
            </div>

            <div className="md:col-span-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">Auto combine after ingest</div>
                <div className="text-sm text-gray-500">
                  {combinedMode
                    ? 'Runs enrich → allocate → rollup at the end of the single daily ingest job.'
                    : 'Runs enrich → allocate → rollup ~2 min after sessions or meter cron.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateForm('auto_pipeline_enabled', !form.auto_pipeline_enabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  form.auto_pipeline_enabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  form.auto_pipeline_enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div className="md:col-span-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">Rules schedule enabled</div>
                <div className="text-sm text-gray-500">
                  Automatic rules for the trading day. Choose a clock time, or start them when combine finishes.
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  const enabled = !form.rules_schedule_enabled
                  if (enabled && !form.rules_schedule) {
                    setForm((prev) => ({
                      ...prev,
                      rules_schedule_enabled: true,
                      rules_schedule: timeValueToDailyCron(dailyCronToTimeValue(null)),
                    }))
                  } else {
                    updateForm('rules_schedule_enabled', enabled)
                  }
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  form.rules_schedule_enabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  form.rules_schedule_enabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div className="md:col-span-2 p-3 bg-gray-50 rounded-lg space-y-3">
              <div className="font-medium text-gray-900">When rules run</div>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="rules_run_mode"
                  className="mt-1"
                  checked={!form.rules_after_pipeline}
                  onChange={() => updateForm('rules_after_pipeline', false)}
                />
                <span>At a set time.</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="rules_run_mode"
                  className="mt-1"
                  checked={form.rules_after_pipeline === true}
                  onChange={() => updateForm('rules_after_pipeline', true)}
                />
                <span>After combine finishes for that trading day. The clock time is not used.</span>
              </label>
              {form.rules_after_pipeline ? (
                <p className="text-sm text-gray-500">
                  Rules start when enrich, allocate, and rollup have finished. A second pipeline pass the same morning does not run them again.
                </p>
              ) : (
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-semibold mt-6">
                    {displayRunOrder.findIndex((x) => x.id === 'rules') + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <DailyRunTimeField
                      label="Rules run time (daily)"
                      value={form.rules_schedule}
                      onChange={(cron) => updateForm('rules_schedule', cron)}
                      timezone={tenantTimezone}
                      fallback="08:30"
                      hint={
                        form.rules_schedule_enabled
                          ? `${formatDailyCronLabel(form.rules_schedule || timeValueToDailyCron(RULES_SCHEDULE_ITEM.fallback), { timezone: tenantTimezone })} — fires at this time even if combine is still running`
                          : 'Set the time here, then turn on Rules schedule enabled to run automatically.'
                      }
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">Require pipeline ready</div>
                <div className="text-sm text-gray-500">
                  Skip rules if meter/sessions/combine not ready for the trading day
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateForm('rules_require_ready', form.rules_require_ready === false)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  form.rules_require_ready !== false ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  form.rules_require_ready !== false ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {(form.rules_last_run_at || form.rules_last_run_status) && (
              <div className="md:col-span-2 text-xs text-gray-600">
                Last rules run:{' '}
                <span className="font-medium">{form.rules_last_run_status || '—'}</span>
                {form.rules_last_run_at ? ` · ${formatDateTime(form.rules_last_run_at)}` : ''}
              </div>
            )}

            <div className="md:col-span-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900">Require patron before gaming imports</div>
                <div className="text-sm text-gray-500">Skip sessions/meter cron if DimPatron has not succeeded recently (EGM runs independently)</div>
              </div>
              <button
                type="button"
                onClick={() => updateForm('require_patron_before_gaming', !form.require_patron_before_gaming)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  form.require_patron_before_gaming !== false ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  form.require_patron_before_gaming !== false ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
              <select
                value={form.timezone || 'Australia/Sydney'}
                onChange={(e) => updateForm('timezone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option>Australia/Sydney</option>
                <option>Australia/Melbourne</option>
                <option>Australia/Brisbane</option>
                <option>Australia/Perth</option>
                <option>Australia/Adelaide</option>
                <option>UTC</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Venue code</label>
              <input
                type="text"
                value={form.venue_code || ''}
                onChange={(e) => updateForm('venue_code', e.target.value.toUpperCase())}
                maxLength={16}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="WT"
              />
              <p className="text-xs text-gray-500 mt-1">Stored on patron and session records. Member # uses the numeric Patron ID from the file.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">File source</label>
              <select
                value={form.source_type || 'local'}
                onChange={(e) => updateForm('source_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="local">Local directory</option>
                <option value="azure_blob">Azure SID blob (TPDataExtracts)</option>
              </select>
            </div>

            {form.source_type === 'azure_blob' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Azure container</label>
                  <input
                    type="text"
                    value={form.azure_blob_container || 'tsg'}
                    onChange={(e) => updateForm('azure_blob_container', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Blob prefix</label>
                  <input
                    type="text"
                    value={form.azure_blob_prefix || 'TPDataExtracts/'}
                    onChange={(e) => updateForm('azure_blob_prefix', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trading day offset</label>
                  <input
                    type="number"
                    min={0}
                    max={7}
                    value={form.trading_day_offset ?? 1}
                    onChange={(e) => updateForm('trading_day_offset', parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Days before run date (1 = yesterday)</p>
                </div>
              </>
            )}
          </div>

          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Enabled feeds</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'egm_enabled', label: 'EGM (DimMachine)' },
                { key: 'patron_enabled', label: 'DimPatron' },
                { key: 'sessions_enabled', label: 'Sessions' },
                { key: 'meter_enabled', label: 'Meter' },
                { key: 'slot_analysis_enabled', label: 'Slot analysis' },
                { key: 'tito_enabled', label: 'TITO Data' },
                { key: 'visitor_enabled', label: 'Visitors' },
              ].map(p => (
                <label key={p.key} className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={!!form[p.key]}
                    onChange={(e) => updateForm(p.key, e.target.checked)}
                    className="mt-0.5 mr-2"
                  />
                  <span className="font-medium text-sm text-gray-900">{p.label}</span>
                </label>
              ))}
            </div>
            <label className="flex items-center mt-3">
              <input
                type="checkbox"
                checked={!!form.import_archived_members}
                onChange={(e) => updateForm('import_archived_members', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Import archived members (INACTIVE profile_status)</span>
            </label>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setForm(config)}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={saveConfig}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save configuration'}
            </button>
          </div>
        </div>
      )}

      {tab === 'templates' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
            Assign the template used by scheduled imports for each feed. To create or edit column mappings, use{' '}
            <Link to="/admin/group-config" className="underline font-medium">
              Group Configuration → Import Mapping
            </Link>.
          </div>

          {FEED_TEMPLATES.map(feed => (
            <div key={feed.key} className="bg-white shadow rounded-lg p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{feed.label}</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Default platform key: <code className="bg-gray-100 px-1">{feed.defaultLabel}</code>
                  </p>
                </div>
                <Link
                  to={feed.feedPath}
                  className="text-sm text-primary-600 hover:underline"
                >
                  Open feed →
                </Link>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled template</label>
                <select
                  value={form[feed.key] != null && form[feed.key] !== '' ? String(form[feed.key]) : ''}
                  onChange={(e) => updateForm(feed.key, e.target.value ? parseInt(e.target.value, 10) : null)}
                  className="w-full max-w-xl px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Platform default ({feed.defaultLabel})</option>
                  {(templatesByType[feed.mappingType] || []).map(t => (
                    <option key={t.id} value={String(t.id)}>
                      {t.template_name}{t.gaming_machine_vendor ? ` — ${t.gaming_machine_vendor}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {feed.mappingType !== 'patron' && (
                <p className="text-xs text-gray-400 mt-2">
                  Leave on &ldquo;Platform default&rdquo; to use the seeded template, or assign a custom one for a different vendor format.
                  Imports automatically switch to template mode when a mapping is assigned.
                </p>
              )}
            </div>
          ))}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={saveConfig}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save templates'}
            </button>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent import runs</h2>
          {history.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ExclamationTriangleIcon className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No import runs yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Run at</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Process</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Imported</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Errors</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map(run => (
                    <tr key={run.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm text-gray-700">{formatDateTime(run.run_at)}</td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{run.process}</td>
                      <td className="px-3 py-2">{statusBadge(run.status)}</td>
                      <td className="px-3 py-2 text-right text-sm">{run.imported || 0}</td>
                      <td className={`px-3 py-2 text-right text-sm ${run.errors > 0 ? 'text-red-600 font-medium' : ''}`}>
                        {run.errors || 0}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {run.triggered_by === 'manual' ? `${run.triggered_by_user || 'admin'} (manual)` : 'cron'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
